import React, { useState, useEffect } from 'react';

const BACKEND_URL = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
  ? 'http://localhost:5001'
  : '';

export default function MetaAccessView() {
  const sessionStr = localStorage.getItem('louder_session');
  let currentUser = null;
  try {
    currentUser = sessionStr ? JSON.parse(sessionStr) : null;
  } catch (e) {}
  const isAdmin = currentUser?.role === 'admin';

  const [agency, setAgency] = useState({ name: 'Louder Operations', businessManagerId: '', users: [] });
  const [requests, setRequests] = useState([]);
  const [clients, setClients] = useState([]);
  const [activeTab, setActiveTab] = useState('requests'); // 'requests' | 'clients'

  // Modals
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [selectedClientForLeads, setSelectedClientForLeads] = useState(null);

  // Form states
  const [newClientName, setNewClientName] = useState('');
  const [newClientEmail, setNewClientEmail] = useState('');
  const [reqLeads, setReqLeads] = useState(true);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [createdLinkResult, setCreatedLinkResult] = useState(null);
  const [copiedSlug, setCopiedSlug] = useState(null);

  // Settings form states
  const [settingAgencyName, setSettingAgencyName] = useState('');
  const [settingBmId, setSettingBmId] = useState('');
  const [settingMetaAppId, setSettingMetaAppId] = useState('');
  const [settingMetaAppSecret, setSettingMetaAppSecret] = useState('');
  const [settingMetaConfigId, setSettingMetaConfigId] = useState('');
  const [settingWebhookVerifyToken, setSettingWebhookVerifyToken] = useState('louder_leads_secret_2026');

  const fetchData = async () => {
    try {
      const [agencyRes, requestsRes, clientsRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/meta-access/agency`),
        fetch(`${BACKEND_URL}/api/meta-access/requests`),
        fetch(`${BACKEND_URL}/api/meta-access/clients`)
      ]);

      const agencyData = await agencyRes.json();
      const requestsData = await requestsRes.json();
      const clientsData = await clientsRes.json();

      if (agencyData.success) {
        setAgency(agencyData.agency);
        setSettingAgencyName(agencyData.agency.name || '');
        setSettingBmId(agencyData.agency.businessManagerId || '');
        setSettingMetaAppId(agencyData.agency.metaAppId || '');
        setSettingMetaConfigId(agencyData.agency.metaConfigId || '');
        setSettingWebhookVerifyToken(agencyData.agency.webhookVerifyToken || 'louder_leads_secret_2026');
      }
      if (requestsData.success) setRequests(requestsData.requests);
      if (clientsData.success) setClients(clientsData.clients);
    } catch (err) {
      console.error('Failed to load Meta Access data:', err);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const getFullPublicUrl = (slug) => {
    const origin = window.location.origin;
    return `${origin}/r/${slug}`;
  };

  const handleCopy = (slug) => {
    navigator.clipboard.writeText(getFullPublicUrl(slug));
    setCopiedSlug(slug);
    setTimeout(() => setCopiedSlug(null), 2000);
  };

  const handleCreateRequest = async (e) => {
    e.preventDefault();
    if (!newClientName.trim()) return;

    try {
      const res = await fetch(`${BACKEND_URL}/api/meta-access/requests`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          clientName: newClientName,
          clientEmail: newClientEmail,
          reqPageAccess: 'MANAGE_ADS',
          reqInstagram: true,
          reqLeads,
          autoAssignUserIds: selectedUserIds
        })
      });
      const data = await res.json();
      if (data.success) {
        setCreatedLinkResult(data.request);
        fetchData();
      } else {
        alert(data.error || 'Failed to create request');
      }
    } catch (err) {
      alert('Error: ' + err.message);
    }
  };

  const handleSaveSettings = async (e) => {
    e.preventDefault();
    if (!isAdmin) {
      alert('Only administrators can edit business and developer settings.');
      return;
    }
    try {
      const res = await fetch(`${BACKEND_URL}/api/meta-access/agency`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'x-requester-email': currentUser?.email || ''
        },
        body: JSON.stringify({
          requesterEmail: currentUser?.email,
          name: settingAgencyName,
          businessManagerId: settingBmId,
          metaAppId: settingMetaAppId,
          metaAppSecret: settingMetaAppSecret,
          metaConfigId: settingMetaConfigId,
          webhookVerifyToken: settingWebhookVerifyToken
        })
      });
      const data = await res.json();
      if (data.success) {
        alert('Business and Meta Developer settings saved successfully!');
        setSettingMetaAppSecret(''); // Clear plain password field after save
        setIsSettingsOpen(false);
        fetchData();
      } else {
        alert(data.error || 'Failed to save settings');
      }
    } catch (err) {
      alert('Error: ' + err.message);
    }
  };

  const handleDisconnectClient = async (id, name) => {
    if (!window.confirm(`Disconnect ${name}?`)) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/meta-access/clients/${id}`, { method: 'DELETE' });
      const data = await res.json();
      if (data.success) fetchData();
    } catch (err) {
      alert('Error: ' + err.message);
    }
  };

  const downloadCSV = (clientName, leads) => {
    if (!leads || leads.length === 0) return;
    const parsed = leads.map(l => {
      let fields = {};
      try { fields = JSON.parse(l.fieldDataJson); } catch (e) {}
      return {
        'Created At': new Date(l.createdTime).toLocaleString(),
        'Ad Name': l.adName || '',
        ...fields
      };
    });
    const headers = Array.from(new Set(parsed.flatMap(item => Object.keys(item))));
    const csvRows = [
      headers.join(','),
      ...parsed.map(row => headers.map(h => `"${String(row[h] || '').replace(/"/g, '""')}"`).join(','))
    ];
    const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `leads-${clientName.toLowerCase().replace(/\s+/g, '-')}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const totalLeads = clients.reduce((acc, curr) => acc + (curr._count?.leadSubmissions || 0), 0);

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f8f7fa', color: '#1f2937', fontFamily: 'system-ui, -apple-system, sans-serif' }}>
      <main style={{ maxWidth: '1200px', margin: '0 auto', padding: '32px 24px' }}>
        {/* Subheader Banner */}
        <div style={{
          backgroundColor: '#ffffff',
          borderRadius: '24px',
          padding: '24px 32px',
          border: '1px solid rgba(15, 9, 28, 0.08)',
          boxShadow: '0 4px 20px rgba(0,0,0,0.03)',
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '20px',
          marginBottom: '28px'
        }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span style={{ fontSize: '24px' }}>🔗</span>
              <h1 style={{ fontSize: '22px', fontWeight: '800', margin: 0, color: '#1f2937' }}>
                Meta Access Hub
              </h1>
              <span style={{ backgroundColor: 'rgba(0, 100, 224, 0.1)', color: '#0064E0', fontSize: '11px', fontWeight: '700', padding: '3px 8px', borderRadius: '12px' }}>
                Leads Tool
              </span>
            </div>
            <p style={{ fontSize: '13px', color: '#6b7280', margin: '4px 0 0 0' }}>
              Generate 1-click links for Artist Managers to link their Facebook Page & Instagram to Louder for Ads, Custom Audiences & Leads.
            </p>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {isAdmin && (
              <button
                onClick={() => setIsSettingsOpen(true)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '10px 16px',
                  backgroundColor: '#ffffff',
                  border: '1px solid rgba(15, 9, 28, 0.12)',
                  borderRadius: '12px',
                  fontWeight: '600',
                  fontSize: '13px',
                  color: '#374151',
                  cursor: 'pointer'
                }}
              >
                ⚙️ BM & Team Settings
              </button>
            )}

            <button
              onClick={() => {
                setCreatedLinkResult(null);
                setNewClientName('');
                setNewClientEmail('');
                setIsCreateOpen(true);
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '10px 18px',
                backgroundColor: '#0064E0',
                color: '#ffffff',
                border: 'none',
                borderRadius: '12px',
                fontWeight: '700',
                fontSize: '13px',
                cursor: 'pointer',
                boxShadow: '0 4px 14px rgba(0, 100, 224, 0.25)'
              }}
            >
              + Create 1-Click Link
            </button>
          </div>
        </div>

        {/* Metrics Grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: '16px',
          marginBottom: '28px'
        }}>
          <div style={{ backgroundColor: '#ffffff', padding: '18px 22px', borderRadius: '18px', border: '1px solid rgba(15, 9, 28, 0.08)' }}>
            <div style={{ fontSize: '11px', fontWeight: '700', color: '#9ca3af', textTransform: 'uppercase' }}>Active Request Links</div>
            <div style={{ fontSize: '24px', fontWeight: '800', color: '#0064E0', marginTop: '4px' }}>{requests.length}</div>
          </div>
          <div style={{ backgroundColor: '#ffffff', padding: '18px 22px', borderRadius: '18px', border: '1px solid rgba(15, 9, 28, 0.08)' }}>
            <div style={{ fontSize: '11px', fontWeight: '700', color: '#9ca3af', textTransform: 'uppercase' }}>Connected Artists</div>
            <div style={{ fontSize: '24px', fontWeight: '800', color: '#10b981', marginTop: '4px' }}>{clients.length}</div>
          </div>
          <div style={{ backgroundColor: '#ffffff', padding: '18px 22px', borderRadius: '18px', border: '1px solid rgba(15, 9, 28, 0.08)' }}>
            <div style={{ fontSize: '11px', fontWeight: '700', color: '#9ca3af', textTransform: 'uppercase' }}>Leads Captured</div>
            <div style={{ fontSize: '24px', fontWeight: '800', color: '#8b5cf6', marginTop: '4px' }}>{totalLeads}</div>
          </div>
          <div style={{ backgroundColor: '#ffffff', padding: '18px 22px', borderRadius: '18px', border: '1px solid rgba(15, 9, 28, 0.08)' }}>
            <div style={{ fontSize: '11px', fontWeight: '700', color: '#9ca3af', textTransform: 'uppercase' }}>Page & IG Sync</div>
            <div style={{ fontSize: '24px', fontWeight: '800', color: '#10b981', marginTop: '4px' }}>100% Active</div>
          </div>
        </div>

        {/* Tab Selector */}
        <div style={{ display: 'flex', gap: '8px', borderBottom: '1px solid rgba(15, 9, 28, 0.08)', paddingBottom: '12px', marginBottom: '20px' }}>
          <button
            onClick={() => setActiveTab('requests')}
            style={{
              padding: '8px 18px',
              borderRadius: '12px',
              border: 'none',
              fontWeight: '700',
              fontSize: '13px',
              cursor: 'pointer',
              backgroundColor: activeTab === 'requests' ? '#1f2937' : 'transparent',
              color: activeTab === 'requests' ? '#ffffff' : '#5b6275'
            }}
          >
            Access Request Links ({requests.length})
          </button>
          <button
            onClick={() => setActiveTab('clients')}
            style={{
              padding: '8px 18px',
              borderRadius: '12px',
              border: 'none',
              fontWeight: '700',
              fontSize: '13px',
              cursor: 'pointer',
              backgroundColor: activeTab === 'clients' ? '#1f2937' : 'transparent',
              color: activeTab === 'clients' ? '#ffffff' : '#5b6275'
            }}
          >
            Connected Assets & Clients ({clients.length})
          </button>
        </div>

        {/* TAB 1: REQUEST LINKS TABLE */}
        {activeTab === 'requests' && (
          <div style={{ backgroundColor: '#ffffff', borderRadius: '20px', border: '1px solid rgba(15, 9, 28, 0.08)', overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '13px' }}>
              <thead>
                <tr style={{ backgroundColor: 'rgba(15, 9, 28, 0.02)', borderBottom: '1px solid rgba(15, 9, 28, 0.08)', color: '#6b7280', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  <th style={{ padding: '14px 20px' }}>Artist / Client</th>
                  <th style={{ padding: '14px 20px' }}>Single Link URL</th>
                  <th style={{ padding: '14px 20px' }}>Requested Permissions</th>
                  <th style={{ padding: '14px 20px' }}>Status</th>
                  <th style={{ padding: '14px 20px', textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {requests.map(req => {
                  const url = getFullPublicUrl(req.slug);
                  const isDone = req.status === 'COMPLETED';
                  return (
                    <tr key={req.id} style={{ borderBottom: '1px solid rgba(15, 9, 28, 0.04)' }}>
                      <td style={{ padding: '16px 20px' }}>
                        <div style={{ fontWeight: '700', color: '#1f2937' }}>{req.clientName}</div>
                        <div style={{ fontSize: '11px', color: '#9ca3af' }}>{req.clientEmail || 'No email specified'}</div>
                      </td>
                      <td style={{ padding: '16px 20px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ fontFamily: 'monospace', fontSize: '12px', backgroundColor: 'rgba(15, 9, 28, 0.04)', padding: '4px 8px', borderRadius: '6px' }}>
                            /r/{req.slug}
                          </span>
                          <button
                            onClick={() => handleCopy(req.slug)}
                            style={{ padding: '4px 8px', fontSize: '11px', fontWeight: '600', backgroundColor: copiedSlug === req.slug ? '#10b981' : '#f3f4f6', color: copiedSlug === req.slug ? '#ffffff' : '#374151', border: 'none', borderRadius: '6px', cursor: 'pointer' }}
                          >
                            {copiedSlug === req.slug ? '✓ Copied' : 'Copy'}
                          </button>
                        </div>
                      </td>
                      <td style={{ padding: '16px 20px' }}>
                        <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                          <span style={{ fontSize: '10px', backgroundColor: '#eff6ff', color: '#1d4ed8', padding: '2px 6px', borderRadius: '4px', fontWeight: '600' }}>Page Ads</span>
                          <span style={{ fontSize: '10px', backgroundColor: '#fdf2f8', color: '#db2777', padding: '2px 6px', borderRadius: '4px', fontWeight: '600' }}>Instagram</span>
                          <span style={{ fontSize: '10px', backgroundColor: '#eef2ff', color: '#4338ca', padding: '2px 6px', borderRadius: '4px', fontWeight: '600' }}>Audiences</span>
                          {req.reqLeads && <span style={{ fontSize: '10px', backgroundColor: '#faf5ff', color: '#7e22ce', padding: '2px 6px', borderRadius: '4px', fontWeight: '600' }}>Leads</span>}
                        </div>
                      </td>
                      <td style={{ padding: '16px 20px' }}>
                        <span style={{
                          fontSize: '11px',
                          fontWeight: '700',
                          padding: '4px 10px',
                          borderRadius: '20px',
                          backgroundColor: isDone ? 'rgba(16, 185, 129, 0.1)' : 'rgba(245, 158, 11, 0.1)',
                          color: isDone ? '#10b981' : '#d97706'
                        }}>
                          {isDone ? '✓ Connected' : '⏳ Pending'}
                        </span>
                      </td>
                      <td style={{ padding: '16px 20px', textAlign: 'right' }}>
                        <a
                          href={url}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ fontSize: '12px', fontWeight: '700', color: '#0064E0', textDecoration: 'none' }}
                        >
                          Open Link ↗
                        </a>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* TAB 2: CONNECTED CLIENTS TABLE */}
        {activeTab === 'clients' && (
          <div style={{ backgroundColor: '#ffffff', borderRadius: '20px', border: '1px solid rgba(15, 9, 28, 0.08)', overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '13px' }}>
              <thead>
                <tr style={{ backgroundColor: 'rgba(15, 9, 28, 0.02)', borderBottom: '1px solid rgba(15, 9, 28, 0.08)', color: '#6b7280', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  <th style={{ padding: '14px 20px' }}>Artist / Client</th>
                  <th style={{ padding: '14px 20px' }}>Facebook Page</th>
                  <th style={{ padding: '14px 20px' }}>Connected Instagram</th>
                  <th style={{ padding: '14px 20px' }}>Lead Forms</th>
                  <th style={{ padding: '14px 20px' }}>Token Health</th>
                  <th style={{ padding: '14px 20px', textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {clients.map(client => (
                  <tr key={client.id} style={{ borderBottom: '1px solid rgba(15, 9, 28, 0.04)' }}>
                    <td style={{ padding: '16px 20px' }}>
                      <div style={{ fontWeight: '700', color: '#1f2937' }}>{client.clientName}</div>
                      <div style={{ fontSize: '11px', color: '#9ca3af' }}>{client.clientEmail}</div>
                    </td>
                    <td style={{ padding: '16px 20px' }}>
                      <div style={{ fontWeight: '600', color: '#1f2937' }}>{client.pageName}</div>
                      <div style={{ fontSize: '11px', color: '#9ca3af', fontFamily: 'monospace' }}>ID: {client.pageId}</div>
                    </td>
                    <td style={{ padding: '16px 20px' }}>
                      {client.instagramName ? (
                        <div style={{ fontWeight: '600', color: '#db2777', display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <span>📸</span>
                          <span>{client.instagramName}</span>
                        </div>
                      ) : (
                        <span style={{ fontSize: '12px', color: '#9ca3af' }}>Auto-linked via Page</span>
                      )}
                    </td>
                    <td style={{ padding: '16px 20px' }}>
                      <button
                        onClick={() => setSelectedClientForLeads(client)}
                        style={{
                          padding: '4px 10px',
                          backgroundColor: 'rgba(139, 92, 246, 0.1)',
                          color: '#7c3aed',
                          border: '1px solid rgba(139, 92, 246, 0.2)',
                          borderRadius: '8px',
                          fontSize: '12px',
                          fontWeight: '700',
                          cursor: 'pointer'
                        }}
                      >
                        👥 {client._count?.leadSubmissions || 0} Leads
                      </button>
                    </td>
                    <td style={{ padding: '16px 20px' }}>
                      <span style={{ fontSize: '11px', fontWeight: '700', color: '#10b981', display: 'flex', alignItems: 'center', gap: '4px' }}>
                        ● 58 days active
                      </span>
                    </td>
                    <td style={{ padding: '16px 20px', textAlign: 'right' }}>
                      <button
                        onClick={() => handleDisconnectClient(client.id, client.clientName)}
                        style={{ padding: '4px 8px', fontSize: '11px', color: '#ef4444', backgroundColor: 'transparent', border: 'none', cursor: 'pointer', fontWeight: '600' }}
                      >
                        Disconnect
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>

      {/* CREATE REQUEST MODAL */}
      {isCreateOpen && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' }}>
          <div style={{ backgroundColor: '#ffffff', borderRadius: '24px', width: '100%', maxWidth: '520px', padding: '32px', boxShadow: '0 20px 50px rgba(0,0,0,0.2)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
              <h3 style={{ fontSize: '18px', fontWeight: '800', margin: 0 }}>
                {createdLinkResult ? '🎉 Single Link Ready' : 'Create 1-Click Meta Link'}
              </h3>
              <button onClick={() => setIsCreateOpen(false)} style={{ border: 'none', background: 'transparent', fontSize: '18px', cursor: 'pointer' }}>✕</button>
            </div>

            {createdLinkResult ? (
              <div style={{ textAlign: 'center', padding: '10px 0' }}>
                <p style={{ fontSize: '13px', color: '#6b7280', margin: '0 0 16px 0' }}>
                  Send this single link to <strong>{createdLinkResult.clientName}</strong>:
                </p>
                <div style={{ display: 'flex', gap: '8px', marginBottom: '20px' }}>
                  <input
                    type="text"
                    readOnly
                    value={getFullPublicUrl(createdLinkResult.slug)}
                    style={{ flex: 1, padding: '10px 14px', borderRadius: '12px', border: '1px solid #d1d5db', fontSize: '13px', fontFamily: 'monospace' }}
                  />
                  <button
                    onClick={() => handleCopy(createdLinkResult.slug)}
                    style={{ padding: '10px 16px', backgroundColor: copiedSlug === createdLinkResult.slug ? '#10b981' : '#0064E0', color: '#ffffff', border: 'none', borderRadius: '12px', fontWeight: '700', cursor: 'pointer' }}
                  >
                    {copiedSlug === createdLinkResult.slug ? '✓ Copied' : 'Copy'}
                  </button>
                </div>
                <button
                  onClick={() => setIsCreateOpen(false)}
                  style={{ width: '100%', padding: '12px', backgroundColor: '#1f2937', color: '#ffffff', borderRadius: '12px', border: 'none', fontWeight: '700', cursor: 'pointer' }}
                >
                  Done
                </button>
              </div>
            ) : (
              <form onSubmit={handleCreateRequest} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: '700', color: '#374151', marginBottom: '6px' }}>Artist / Client Name *</label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. Billie Eilish / Universal Music"
                    value={newClientName}
                    onChange={e => setNewClientName(e.target.value)}
                    style={{ width: '100%', padding: '10px 14px', borderRadius: '12px', border: '1px solid #d1d5db', fontSize: '13px', boxSizing: 'border-box' }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: '700', color: '#374151', marginBottom: '6px' }}>Manager Email (Optional)</label>
                  <input
                    type="email"
                    placeholder="mgmt@artist.com"
                    value={newClientEmail}
                    onChange={e => setNewClientEmail(e.target.value)}
                    style={{ width: '100%', padding: '10px 14px', borderRadius: '12px', border: '1px solid #d1d5db', fontSize: '13px', boxSizing: 'border-box' }}
                  />
                </div>

                <div style={{ backgroundColor: 'rgba(15, 9, 28, 0.02)', padding: '14px', borderRadius: '14px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <div style={{ fontSize: '11px', fontWeight: '700', color: '#6b7280', textTransform: 'uppercase' }}>Assets & Permissions Included</div>
                  <div style={{ fontSize: '12px', color: '#166534', fontWeight: '600' }}>
                    ✓ Facebook Page & Connected Instagram Ads
                  </div>
                  <div style={{ fontSize: '12px', color: '#166534', fontWeight: '600' }}>
                    ✓ Custom Audiences (Fan engagement tracking)
                  </div>
                  <label style={{ fontSize: '12px', display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', marginTop: '4px' }}>
                    <input type="checkbox" checked={reqLeads} onChange={e => setReqLeads(e.target.checked)} />
                    Lead Form Submissions & Webhook Sync
                  </label>
                </div>

                {/* Auto-Assign Internal Team Members */}
                {agency.users && agency.users.length > 0 && (
                  <div style={{ backgroundColor: 'rgba(15, 9, 28, 0.02)', padding: '14px', borderRadius: '14px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div style={{ fontSize: '11px', fontWeight: '700', color: '#6b7280', textTransform: 'uppercase' }}>
                        Auto-Assign Louder Team Members
                      </div>
                      <span style={{ fontSize: '10px', color: '#9ca3af' }}>Site-Wide Users</span>
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                      {agency.users.map(u => {
                        const isSelected = selectedUserIds.includes(u.email || u.id);
                        return (
                          <button
                            key={u.id || u.email}
                            type="button"
                            onClick={() => {
                              const id = u.email || u.id;
                              setSelectedUserIds(prev =>
                                prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
                              );
                            }}
                            style={{
                              padding: '6px 10px',
                              borderRadius: '8px',
                              border: isSelected ? '1px solid #0064E0' : '1px solid #d1d5db',
                              backgroundColor: isSelected ? 'rgba(0, 100, 224, 0.1)' : '#ffffff',
                              color: isSelected ? '#0064E0' : '#374151',
                              fontSize: '11px',
                              fontWeight: '600',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px'
                            }}
                          >
                            <span>{isSelected ? '✓' : '+'}</span>
                            <span>{u.name || u.email}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                <button
                  type="submit"
                  style={{ width: '100%', padding: '14px', backgroundColor: '#0064E0', color: '#ffffff', borderRadius: '14px', border: 'none', fontWeight: '800', fontSize: '13px', cursor: 'pointer' }}
                >
                  Generate Single Link ➔
                </button>
              </form>
            )}
          </div>
        </div>
      )}

      {/* AGENCY SETTINGS MODAL */}
      {isSettingsOpen && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' }}>
          <div style={{ backgroundColor: '#ffffff', borderRadius: '24px', width: '100%', maxWidth: '520px', padding: '32px', maxHeight: '85vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
              <h3 style={{ fontSize: '18px', fontWeight: '800', margin: 0 }}>Business Manager & Team Settings</h3>
              <button onClick={() => setIsSettingsOpen(false)} style={{ border: 'none', background: 'transparent', fontSize: '18px', cursor: 'pointer' }}>✕</button>
            </div>

            <form onSubmit={handleSaveSettings} style={{ display: 'flex', flexDirection: 'column', gap: '14px', marginBottom: '24px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '700', color: '#374151', marginBottom: '6px' }}>Agency Name</label>
                <input
                  type="text"
                  value={settingAgencyName}
                  onChange={e => setSettingAgencyName(e.target.value)}
                  style={{ width: '100%', padding: '10px 14px', borderRadius: '12px', border: '1px solid #d1d5db', fontSize: '13px', boxSizing: 'border-box' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '700', color: '#374151', marginBottom: '6px' }}>Meta Business Portfolio ID (BM ID)</label>
                <input
                  type="text"
                  placeholder="e.g. 294810482910394"
                  value={settingBmId}
                  onChange={e => setSettingBmId(e.target.value)}
                  style={{ width: '100%', padding: '10px 14px', borderRadius: '12px', border: '1px solid #d1d5db', fontSize: '13px', fontFamily: 'monospace', boxSizing: 'border-box' }}
                />
              </div>

              <div style={{ backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0', padding: '14px', borderRadius: '14px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: '12px', fontWeight: '800', color: '#166534', textTransform: 'uppercase' }}>⚡ Meta Developer Credentials</span>
                  <span style={{ fontSize: '10px', backgroundColor: '#dcfce7', color: '#15803d', fontWeight: '700', padding: '2px 6px', borderRadius: '4px' }}>AES-256 Encrypted</span>
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: '#374151', marginBottom: '4px' }}>Meta App ID</label>
                  <input
                    type="text"
                    placeholder="e.g. 1829481029481"
                    value={settingMetaAppId}
                    onChange={e => setSettingMetaAppId(e.target.value)}
                    style={{ width: '100%', padding: '8px 12px', borderRadius: '10px', border: '1px solid #d1d5db', fontSize: '12px', fontFamily: 'monospace', boxSizing: 'border-box', backgroundColor: '#ffffff' }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: '#374151', marginBottom: '4px' }}>Meta App Secret</label>
                  <input
                    type="password"
                    placeholder={agency.hasMetaSecret ? "•••••••• (Stored Encrypted)" : "Paste App Secret from Meta Developer Console"}
                    value={settingMetaAppSecret}
                    onChange={e => setSettingMetaAppSecret(e.target.value)}
                    style={{ width: '100%', padding: '8px 12px', borderRadius: '10px', border: '1px solid #d1d5db', fontSize: '12px', fontFamily: 'monospace', boxSizing: 'border-box', backgroundColor: '#ffffff' }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: '#374151', marginBottom: '4px' }}>Login Configuration ID (config_id) (Optional)</label>
                  <input
                    type="text"
                    placeholder="e.g. 1566368704779162"
                    value={settingMetaConfigId}
                    onChange={e => setSettingMetaConfigId(e.target.value)}
                    style={{ width: '100%', padding: '8px 12px', borderRadius: '10px', border: '1px solid #d1d5db', fontSize: '12px', fontFamily: 'monospace', boxSizing: 'border-box', backgroundColor: '#ffffff' }}
                  />
                  <span style={{ fontSize: '10px', color: '#166534', marginTop: '2px', display: 'block' }}>Enforces your custom asset rules (e.g. Current Pages only) from Facebook Login for Business &gt; Configurations.</span>
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: '#374151', marginBottom: '4px' }}>Webhook Verify Token</label>
                  <input
                    type="text"
                    value={settingWebhookVerifyToken}
                    onChange={e => setSettingWebhookVerifyToken(e.target.value)}
                    style={{ width: '100%', padding: '8px 12px', borderRadius: '10px', border: '1px solid #d1d5db', fontSize: '12px', fontFamily: 'monospace', boxSizing: 'border-box', backgroundColor: '#ffffff' }}
                  />
                </div>

                <div style={{ borderTop: '1px solid #bbf7d0', paddingTop: '10px', display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '11px', color: '#166534' }}>
                  <div>
                    <strong>OAuth Redirect URI:</strong>
                    <div style={{ fontFamily: 'monospace', backgroundColor: '#ffffff', padding: '6px 8px', borderRadius: '6px', border: '1px solid #cbd5e1', marginTop: '3px', wordBreak: 'break-all', userSelect: 'all' }}>
                      {window.location.origin}/api/meta-access/callback
                    </div>
                  </div>
                  <div>
                    <strong>Webhook Callback URL:</strong>
                    <div style={{ fontFamily: 'monospace', backgroundColor: '#ffffff', padding: '6px 8px', borderRadius: '6px', border: '1px solid #cbd5e1', marginTop: '3px', wordBreak: 'break-all', userSelect: 'all' }}>
                      {window.location.origin}/api/meta-access/webhooks/meta
                    </div>
                  </div>
                </div>
              </div>

              <button type="submit" style={{ padding: '12px', backgroundColor: '#1f2937', color: '#ffffff', borderRadius: '12px', border: 'none', fontWeight: '700', cursor: 'pointer' }}>
                Save Business & Developer Settings
              </button>
            </form>

            <div style={{ borderTop: '1px solid rgba(15, 9, 28, 0.08)', paddingTop: '20px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <div>
                  <h4 style={{ fontSize: '13px', fontWeight: '700', margin: 0 }}>Site-Wide Team Directory</h4>
                  <p style={{ fontSize: '11px', color: '#6b7280', margin: '2px 0 0 0' }}>These active staff members are auto-assigned when clients grant access.</p>
                </div>
                <button
                  onClick={() => {
                    setIsSettingsOpen(false);
                    window.location.href = '/admin';
                  }}
                  style={{ padding: '6px 12px', backgroundColor: 'rgba(217, 119, 6, 0.1)', color: '#d97706', border: '1px solid rgba(217, 119, 6, 0.2)', borderRadius: '8px', fontSize: '11px', fontWeight: '700', cursor: 'pointer' }}
                >
                  Manage Users ➔
                </button>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {(agency.users || []).map(u => (
                  <div key={u.id || u.email} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', backgroundColor: '#f9fafb', borderRadius: '8px', fontSize: '12px' }}>
                    <div>
                      <span style={{ fontWeight: '700', color: '#1f2937' }}>{u.name}</span>
                      <span style={{ color: '#9ca3af', marginLeft: '6px' }}>({u.email})</span>
                    </div>
                    <span style={{ fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', backgroundColor: u.role === 'admin' ? '#fef3c7' : '#f3f4f6', color: u.role === 'admin' ? '#d97706' : '#6b7280', padding: '2px 6px', borderRadius: '4px' }}>
                      {u.role || 'member'}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* LEADS DRAWER */}
      {selectedClientForLeads && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'flex-end', zIndex: 1000 }}>
          <div style={{ backgroundColor: '#ffffff', width: '100%', maxWidth: '580px', height: '100%', padding: '32px', overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid rgba(15, 9, 28, 0.08)', paddingBottom: '16px', marginBottom: '20px' }}>
              <div>
                <h3 style={{ fontSize: '18px', fontWeight: '800', margin: 0 }}>
                  Captured Leads: {selectedClientForLeads.clientName}
                </h3>
                <span style={{ fontSize: '12px', color: '#6b7280' }}>
                  {selectedClientForLeads.leadSubmissions?.length || 0} Submissions Captured
                </span>
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                {selectedClientForLeads.leadSubmissions?.length > 0 && (
                  <button
                    onClick={() => downloadCSV(selectedClientForLeads.clientName, selectedClientForLeads.leadSubmissions)}
                    style={{ padding: '8px 14px', backgroundColor: '#10b981', color: '#ffffff', borderRadius: '10px', border: 'none', fontWeight: '700', fontSize: '12px', cursor: 'pointer' }}
                  >
                    Export CSV ⬇
                  </button>
                )}
                <button onClick={() => setSelectedClientForLeads(null)} style={{ border: 'none', background: 'transparent', fontSize: '18px', cursor: 'pointer' }}>✕</button>
              </div>
            </div>

            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '14px' }}>
              {(selectedClientForLeads.leadSubmissions || []).map(lead => {
                let fields = {};
                try { fields = JSON.parse(lead.fieldDataJson); } catch (e) {}
                return (
                  <div key={lead.id} style={{ padding: '16px', borderRadius: '16px', border: '1px solid rgba(15, 9, 28, 0.08)', backgroundColor: '#fdfdfe' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: '#9ca3af', marginBottom: '8px' }}>
                      <span style={{ fontWeight: '700', color: '#0064E0' }}>{lead.adName || 'Lead Ad'}</span>
                      <span>{new Date(lead.createdTime).toLocaleString()}</span>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px', fontSize: '12px' }}>
                      {Object.entries(fields).map(([k, v]) => (
                        <div key={k} style={{ backgroundColor: 'rgba(15, 9, 28, 0.03)', padding: '6px 10px', borderRadius: '8px' }}>
                          <div style={{ fontSize: '10px', color: '#9ca3af', textTransform: 'uppercase', fontWeight: '700' }}>{k.replace(/_/g, ' ')}</div>
                          <div style={{ fontWeight: '600', color: '#1f2937' }}>{String(v)}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
